import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Camera, Check, ChevronRight, MapPin, SkipForward, X } from "lucide-react";

import {
  useAddIngestionPhoto,
  useAnalyseIngestion,
  useChooseIngestionProduct,
  useConfirmIngestion,
  useIngestionSession,
  useSetIngestionFields,
  useSetIngestionQuantity,
  useStartIngestion,
} from "@/api/queries";
import type { Uuid } from "@/api/types";
import { Button } from "@/components/Button";
import { Input } from "@/components/Input";
import { LoadingState } from "@/components/States";
import { Photo } from "@/components/Photo";
import { useToast } from "@/components/toast-context";
import { reportMutationError } from "@/lib/mutation-errors";
import { ConfidenceIndicator } from "./ConfidenceIndicator";
import { DegradedNotice } from "./DegradedNotice";
import { PlacePicker } from "./PlacePicker";
import { QuantityControl } from "./QuantityControl";

/**
 * Blitz mode (TICKET FE-015, Master Context §11).
 *
 * Full screen, one hand, mobile first. The destination is chosen once and stays
 * put — that is the whole point: photograph, confirm, next, fifty times, without
 * re-picking the shelf or opening a product sheet.
 */
export function BlitzPage() {
  const navigate = useNavigate();
  const toast = useToast();

  const [place, setPlace] = useState<{ location_id: Uuid | null; container_id: Uuid | null }>({
    location_id: null,
    container_id: null,
  });
  const [sessionId, setSessionId] = useState<Uuid | null>(null);
  const [counted, setCounted] = useState(0);

  const start = useStartIngestion();

  function openSession() {
    start.mutate(
      { mode: "BLITZ", location_id: place.location_id, container_id: place.container_id },
      {
        onSuccess: (session) => setSessionId(session.id),
        onError: () => toast.error("Impossible de démarrer le mode Blitz"),
      },
    );
  }

  // Pick the destination first, once. Everything after that is capture and confirm.
  if (!place.location_id && !place.container_id) {
    return (
      <div className="mx-auto flex min-h-0 max-w-md flex-1 flex-col justify-center gap-4 px-4">
        <div className="text-center">
          <h1 className="text-xl font-semibold text-ink">Mode Blitz</h1>
          <p className="mt-1 text-[13px] text-ink-subtle">
            Choisissez d'abord où vous inventoriez. L'emplacement restera sélectionné pour
            tous les articles suivants.
          </p>
        </div>
        <PlacePicker
          locationId={place.location_id}
          containerId={place.container_id}
          onChange={setPlace}
          label="Où inventoriez-vous ?"
        />
        <Button variant="ghost" onClick={() => navigate("/dashboard")}>
          Quitter
        </Button>
      </div>
    );
  }

  if (!sessionId) {
    if (!start.isPending) openSession();
    return <LoadingState label="Préparation…" />;
  }

  return (
    <BlitzCapture
      sessionId={sessionId}
      place={place}
      counted={counted}
      onChangePlace={() => {
        setSessionId(null);
        setPlace({ location_id: null, container_id: null });
      }}
      onNext={() => {
        setCounted((current) => current + 1);
        setSessionId(null);
        openSession();
      }}
      onQuit={() => navigate("/inventory")}
    />
  );
}

function BlitzCapture({
  sessionId,
  place,
  counted,
  onChangePlace,
  onNext,
  onQuit,
}: {
  sessionId: Uuid;
  place: { location_id: Uuid | null; container_id: Uuid | null };
  counted: number;
  onChangePlace: () => void;
  onNext: () => void;
  onQuit: () => void;
}) {
  const toast = useToast();
  const { data: session } = useIngestionSession(sessionId);
  const addPhoto = useAddIngestionPhoto();
  const analyse = useAnalyseIngestion();
  const setFields = useSetIngestionFields();
  const chooseProduct = useChooseIngestionProduct();
  const setQuantity = useSetIngestionQuantity();
  const confirm = useConfirmIngestion();

  const [name, setName] = useState("");
  const [quantity, setQuantityValue] = useState("1");
  const fileInput = useRef<HTMLInputElement>(null);
  const idempotencyKey = useRef(`blitz-${sessionId}`);

  useEffect(() => {
    if (session?.values.name) setName(session.values.name);
  }, [session]);

  const topCandidate = session?.candidates[0];
  const productId = session?.resolved_product_id ?? topCandidate?.product_id ?? null;
  const canConfirm = Boolean(productId || name.trim());

  async function confirmAndNext() {
    if (productId) {
      await chooseProduct.mutateAsync({ sessionId, product_id: productId });
    } else {
      await setFields.mutateAsync({ sessionId, values: { name } });
      await chooseProduct.mutateAsync({ sessionId, create_new: true });
    }
    await setQuantity.mutateAsync({ sessionId, quantity });
    await confirm.mutateAsync({ sessionId, idempotencyKey: idempotencyKey.current });
    toast.success("Ajouté", `${counted + 1} article(s) dans cette session.`);
    setName("");
    setQuantityValue("1");
    onNext();
  }

  return (
    <div className="mx-auto flex min-h-0 w-full max-w-lg flex-1 flex-col gap-3">
      {/* The destination, always visible: in Blitz mode the single most costly
          mistake is counting fifty things into the wrong bin. */}
      <div className="flex items-center gap-2 rounded-lg border border-border bg-surface px-3 py-2">
        <MapPin className="size-4 shrink-0 text-brand" aria-hidden />
        <span className="min-w-0 flex-1 truncate text-[13px] text-ink">
          {place.container_id ? "Contenant sélectionné" : "Emplacement sélectionné"}
        </span>
        <Button variant="ghost" size="sm" onClick={onChangePlace}>
          Changer
        </Button>
      </div>

      <div className="flex min-w-0 flex-col gap-3">
        {session && session.media.length > 0 ? (
          <Photo
            mediaId={session.media[0]!.media_id}
            alt="Article en cours"
            className="aspect-square w-full"
            rounded="rounded-lg"
          />
        ) : (
          <button
            type="button"
            onClick={() => fileInput.current?.click()}
            className="flex aspect-square w-full flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed border-border bg-surface text-ink-subtle active:bg-surface-hover"
          >
            <Camera className="size-12" aria-hidden />
            <span className="text-sm">Photographier l'article</span>
            <span className="text-[12px]">ou saisissez simplement son nom plus bas</span>
          </button>
        )}

        <input
          ref={fileInput}
          type="file"
          accept="image/*"
          capture="environment"
          className="sr-only"
          onChange={(event) => {
            const file = event.target.files?.[0];
            if (!file) return;
            addPhoto.mutate(
              { sessionId, file },
              {
                onSuccess: () => analyse.mutate({ sessionId }),
                onError: () => toast.error("Photo non envoyée"),
              },
            );
            event.target.value = "";
          }}
        />

        {session && session.status !== "DRAFT" && (
          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between gap-2 rounded-lg border border-border bg-surface px-3 py-2">
              <ConfidenceIndicator level={session.confidence} />
              {topCandidate && (
                <span className="min-w-0 flex-1 truncate text-right text-[13px] text-ink">
                  {topCandidate.product_name}
                </span>
              )}
            </div>
            {/* Same panel as Smart Input, on purpose. A model outage must not
                throw the operator out of a Blitz run: the article is still
                identified by its barcode or its label, and the next photo is
                one tap away. */}
            <DegradedNotice
              session={session}
              retrying={analyse.isPending}
              onRetryVision={() =>
                analyse.mutate(
                  { sessionId, retryVision: true },
                  { onError: () => toast.error("Analyse impossible") },
                )
              }
            />
          </div>
        )}

        {!productId && (
          <Input
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="Nom de l'article"
            aria-label="Nom de l'article"
            className="h-12 text-base"
          />
        )}

        <div className="rounded-lg border border-border bg-surface p-3">
          <p className="mb-2 text-[12px] text-ink-muted">Quantité</p>
          <QuantityControl value={quantity} onChange={setQuantityValue} />
        </div>
      </div>

      {/* Thumb-reachable actions: this is used one-handed on a ladder. */}
      <div className="flex shrink-0 gap-2 pb-2">
        <Button variant="ghost" size="lg" onClick={onQuit} aria-label="Quitter">
          <X className="size-5" aria-hidden />
        </Button>
        <Button variant="secondary" size="lg" onClick={onNext} aria-label="Ignorer cet article">
          <SkipForward className="size-5" aria-hidden />
          Ignorer
        </Button>
        <Button
          variant="primary"
          size="lg"
          className="flex-1"
          disabled={!canConfirm}
          loading={confirm.isPending}
          onClick={() => {
            void confirmAndNext().catch((caught) =>
              reportMutationError(toast, "Confirmation impossible", caught),
            );
          }}
        >
          <Check className="size-5" aria-hidden />
          Confirmer
          <ChevronRight className="size-5" aria-hidden />
        </Button>
      </div>
    </div>
  );
}
